import React, { useState, useEffect, useCallback, useRef } from 'react';
import { QuotesContext } from './QuotesContextObject';
import { Quote, QuoteWithRelations, useRealtimeSubscription, QuoteStatus, QUOTE_STATUS, Cache, RequestDeduplicator } from '@cleanpro/common-ui';
import { fetchQuotes, fetchQuoteById, createQuote, updateQuote } from '../api';
import { QuotesContextType } from '../types';

// Create cache and deduplicator instances (shared across component instances)
const quotesCache = new Cache<Quote[]>(5 * 60 * 1000); // 5 minutes TTL
const quoteByIdCache = new Cache<Quote>(5 * 60 * 1000); // 5 minutes TTL
const deduplicator = new RequestDeduplicator();

/**
 * Provides quote data and actions to child components via React Context.
 *
 * This provider manages the state and CRUD operations for quotes, including:
 * - Fetching quotes from the API on mount (with caching)
 * - Adding new quotes
 * - Updating existing quotes (including status changes)
 * - Refreshing the quotes list
 * - Handling loading and error states
 * - Request deduplication to prevent duplicate API calls
 *
 * The context value exposes:
 * - `quotes`: The list of quotes
 * - `loading`: Whether a quote operation is in progress
 * - `error`: Any error message encountered during operations
 * - `addQuote`: Function to add a new quote
 * - `updateQuote`: Function to update an existing quote
 * - `updateQuoteStatus`: Function to update the status of a quote
 * - `refreshQuotes`: Function to re-fetch the quotes from the API
 *
 * @param {object} props - The component props
 * @param {React.ReactNode} props.children - Child components to receive the context
 * @returns {JSX.Element} The provider wrapping its children
 */
export const QuoteDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchTimeRef = useRef<number>(0);

  const refreshQuotes = useCallback(async (force = false) => {
    // Check cache first (unless force refresh)
    if (!force) {
      const cached = quotesCache.get('all-quotes');
      if (cached) {
        console.log('✅ Using cached quotes:', cached.length, 'quotes');
        setQuotes(cached);
        setLoading(false);
        setError(null);
        return;
      }
    }

    // Deduplicate concurrent requests
    return deduplicator.deduplicate('fetch-all-quotes', async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchQuotes(); // Fetch quotes from the API
        
        // Update cache
        quotesCache.set('all-quotes', data, { tags: ['quotes'] });
        lastFetchTimeRef.current = Date.now();
        
        setQuotes(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load quotes');
      } finally {
        setLoading(false);
      }
    });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadQuotes = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchQuotes();
        if (isMounted) {
          setQuotes(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load quotes');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadQuotes();

    return () => {
      isMounted = false;
    };
  }, []);

  // Setup real-time subscription
  useRealtimeSubscription({
    table: 'quotes',
    filter: 'status=neq.archived',
    callbacks: {
      onInsert: async () => {
        // For quotes, we need to fetch customer info, so we'll refresh the list
        // Alternatively, we could fetch just the new quote with customer data
        try {
          const data = await fetchQuotes();
          if (data) {
            setQuotes(data);
          }
        } catch (err) {
          console.error('Error refreshing quotes after insert:', err);
        }
      },
      onUpdate: async () => {
        // Refresh quotes to get updated customer relations
        try {
          const data = await fetchQuotes();
          if (data) {
            setQuotes(data);
          }
        } catch (err) {
          console.error('Error refreshing quotes after update:', err);
        }
      },
      onDelete: (payload) => {
        setQuotes((prev) => prev.filter((quote) => quote.id !== (payload.old as Quote).id));
      },
    },
  });

  const addQuote = async (quote: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      setLoading(true);
      const newQuote = await createQuote(quote);
      setQuotes((prev) => [newQuote, ...prev]);
      
      // Invalidate cache after mutation
      quotesCache.invalidateByTag('quotes');
      quoteByIdCache.set(newQuote.id, newQuote, { tags: ['quotes', `quote-${newQuote.id}`] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create quote');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteInState = async (quoteId: string, quoteData: Partial<Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      setLoading(true);
      const updatedQuote = await updateQuote(quoteId, quoteData);
      setQuotes((prev) => prev.map((q) => (q.id === quoteId ? updatedQuote : q)));
      
      // Invalidate cache after mutation
      quotesCache.invalidateByTag('quotes');
      quoteByIdCache.invalidateByTag(`quote-${quoteId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update quote');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId: string, status: QuoteStatus) => {
    try {
      const updateData: Partial<Quote> = { status };
      
      // Add timestamp fields based on status
      const now = new Date().toISOString();
      if (status === QUOTE_STATUS.AWAITING_RESPONSE) {
        updateData.sentAt = now;
      } else if (status === QUOTE_STATUS.CHANGES_REQUESTED) {
        updateData.viewedAt = now;
      } else if (status === QUOTE_STATUS.APPROVED) {
        updateData.acceptedAt = now;
      } else if (status === QUOTE_STATUS.ARCHIVED) {
        updateData.cancelledAt = now;
      }

      await updateQuoteInState(quoteId, updateData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update quote status');
      throw err;
    }
  };

  const getQuoteById = async (quoteId: string): Promise<QuoteWithRelations | null> => {
    try {
      // Check cache first
      const cached = quoteByIdCache.get(quoteId);
      if (cached) {
        console.log('✅ Using cached quote:', quoteId);
        return cached as QuoteWithRelations;
      }

      // Check in-memory quotes
      const inMemory = quotes.find(q => q.id === quoteId);
      if (inMemory) {
        return inMemory as QuoteWithRelations;
      }

      // Fetch from API
      const quote = await fetchQuoteById(quoteId);
      if (quote) {
        quoteByIdCache.set(quoteId, quote, { tags: ['quotes', `quote-${quoteId}`] });
      }
      return quote as QuoteWithRelations | null;
    } catch (err) {
      console.error('Error fetching quote by ID:', err);
      throw err;
    }
  };

  const deleteQuote = async (_quoteId: string) => {
    // Placeholder for delete functionality
    console.warn('deleteQuote not yet implemented');
    throw new Error('Delete quote functionality not yet implemented');
  };

  const value: QuotesContextType = {
    quotes,
    loading,
    error,
    addQuote,
    updateQuote: updateQuoteInState,
    updateQuoteStatus,
    getQuoteById,
    deleteQuote,
    refreshQuotes
  };

  return (
    <QuotesContext.Provider value={value}>
      {children}
    </QuotesContext.Provider>
  );
};